import { z } from 'zod';

/* ---------------------------------------------------------------------------
   חוזה ה-API.

   מקור האמת הוא ה-OpenAPI שהשרת מייצר ב-/docs/openapi.json. הסכמות
   כאן מוגדרות ידנית בכוונה, כשכבת אימות שנייה: הן תופסות סחיפה בין
   שרת ללקוח בזמן ריצה, לא רק בזמן בנייה.
   --------------------------------------------------------------------------- */

export const taskStatusSchema = z.enum(['NEW', 'ASSIGNED', 'IN_PROGRESS', 'CLOSED', 'CANCELLED']);
export type TaskStatus = z.infer<typeof taskStatusSchema>;

export const userRoleSchema = z.enum(['OWNER', 'MANAGER', 'VIEWER', 'FIELD']);
export type UserRole = z.infer<typeof userRoleSchema>;

export const verticalSchema = z.enum(['MAINTENANCE', 'CARPENTRY', 'RETAIL']);

export const TRADE_KEYS = [
  'HVAC',
  'ELECTRICAL',
  'PLUMBING',
  'CARPENTRY',
  'RENOVATION',
  'BUILDING_MAINTENANCE',
  'CLEANING',
  'GARDENING',
  'RETAIL',
  'OTHER',
  'OFFICE',
] as const;
export const tradeSchema = z.enum(TRADE_KEYS);
export type TradeKey = z.infer<typeof tradeSchema>;

/** כסף מגיע כמחרוזת, לא כ-number. ראו formatCurrency. */
const money = z.string().regex(/^-?\d+(\.\d{1,2})?$/, 'invalid decimal');

export const checklistItemSchema = z.object({
  label: z.string(),
  done: z.boolean().default(false),
  priceCode: z.string().nullish(),
  sku: z.string().nullish(),
  qty: z.number().int().positive().nullish(),
  // כמות לחיוב מהצעת מחיר עם כמויות. חייבת לשרוד את הסגירה מהמסך.
  priceQty: z.number().int().positive().nullish(),
});
export type ChecklistItem = z.infer<typeof checklistItemSchema>;

export const customerSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  email: z.string().nullish(),
  phone: z.string().nullish(),
  address: z.string().nullish(),
  taxId: z.string().nullish(),
  billingMode: z.enum(['MONTHLY', 'PER_JOB']).nullish(),
  /** הלקוח השיב STOP / הסר — אין אליו הודעות WhatsApp אוטומטיות. */
  whatsappOptOutAt: z.string().nullish(),
  createdAt: z.string(),
});
export type Customer = z.infer<typeof customerSchema>;

export const userSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  role: userRoleSchema,
});

export const taskSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  description: z.string().nullish(),
  status: taskStatusSchema,
  priority: z.number().int(),
  // חייב לכלול כל מקור שהשרת יכול להחזיר. ערך לא מוכר מפיל את הפענוח של
  // הרשימה כולה, לא רק של המשימה הזו.
  source: z.enum(['EMAIL', 'MANUAL', 'CUSTOMER_LINK', 'QUOTE']),
  customerId: z.string().uuid(),
  customer: customerSchema.nullish(),
  assignedToUserId: z.string().uuid().nullish(),
  assignedTo: userSummarySchema.nullish(),
  checklist: z.array(checklistItemSchema).nullish(),
  createdAt: z.string(),
  closedAt: z.string().nullish(),
  scheduledStart: z.string().nullish(),
  scheduledEnd: z.string().nullish(),
  customerConfirmedAt: z.string().nullish(),
  rescheduleRequest: z.string().nullish(),
  rescheduleRequestedAt: z.string().nullish(),
  onTheWayAt: z.string().nullish(),
  /** JOB = ביקור אצל לקוח; ADMIN = משימה למנהל (הזמנת חומרים, ייצור, תיאום). */
  kind: z.enum(['JOB', 'ADMIN']).catch('JOB').default('JOB'),
  dueAt: z.string().nullish(),
  originQuoteId: z.string().uuid().nullish(),
  visitIndex: z.number().nullish(),
  visitCount: z.number().nullish(),
});
export type Task = z.infer<typeof taskSchema>;

/** עימוד keyset — cursor, לא offset. offset נשבר כששורות נוספות בזמן דפדוף. */
export const paginated = <T extends z.ZodTypeAny>(item: T) =>
  z.object({ items: z.array(item), nextCursor: z.string().nullish() });

export const taskListSchema = paginated(taskSchema);

/** GET /tasks/:id — כולל relations שאינם ברשימה. */
export const taskDetailSchema = taskSchema.extend({
  jobTypeTemplate: z
    .object({ id: z.string().uuid(), name: z.string(), requiredSkill: z.string().nullish() })
    .nullish(),
  updatedAt: z.string().nullish(),
});
export type TaskDetail = z.infer<typeof taskDetailSchema>;
export const customerListSchema = paginated(customerSchema);

export const inventoryItemSchema = z.object({
  id: z.string().uuid(),
  sku: z.string(),
  name: z.string(),
  quantity: z.number().int(),
  lowStockThreshold: z.number().int(),
  unitPrice: money.nullish(),
  unitCost: money.nullish(),
  category: z.string().nullish(),
});
export type InventoryItem = z.infer<typeof inventoryItemSchema>;

export const invoiceSchema = z.object({
  id: z.string().uuid(),
  invoiceNumber: z.number().int(),
  customerId: z.string().uuid(),
  customer: customerSchema.nullish(),
  periodStart: z.string(),
  periodEnd: z.string(),
  totalAmount: money,
  status: z.enum(['DRAFT', 'FINALIZED', 'VOID']),
  createdAt: z.string(),
  // חשבונית ירוקה (ADR 0004). null = הופקה אצלנו.
  externalProvider: z.string().nullish(),
  externalNumber: z.string().nullish(),
  allocationNumber: z.string().nullish(),
  externalUrl: z.string().nullish(),
  externalError: z.string().nullish(),
});
export type Invoice = z.infer<typeof invoiceSchema>;

const sessionUserSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  email: z.string(),
  role: userRoleSchema,
  mustChangePassword: z.boolean(),
});

const sessionTenantSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  subdomain: z.string(),
  vertical: verticalSchema,
  // שרת ישן בלי התחום — HVAC, כמו ההתנהגות הקודמת.
  trade: tradeSchema.default('HVAC'),
  /** מה מוצג לעסק (lib/features.ts). חסר בסשן ישן — ברירת המחדל של התחום. */
  features: z.array(z.string()).optional(),
  /** שפת הממשק (lib/i18n.ts). חסר בסשן ישן — עברית. */
  locale: z.enum(['he', 'en']).catch('he').default('he'),
});

/** בלוק החיוב בסשן: סטטוס, קריאה בלבד, ימי ניסיון. */
export const sessionBillingSchema = z.object({
  status: z.enum(['TRIALING', 'ACTIVE', 'PAST_DUE', 'CANCELED', 'EXPIRED']),
  plan: z.enum(['STARTER', 'TEAM', 'GROWTH', 'BUSINESS']),
  readOnly: z.boolean(),
  reason: z.enum(['TRIAL_ENDED', 'PAST_DUE', 'EXPIRED', 'CANCELED']).nullable(),
  trialDaysLeft: z.number().int().nullable(),
  graceEndsAt: z.string().nullable(),
});
export type SessionBilling = z.infer<typeof sessionBillingSchema>;

/** GET /auth/me — סשן ללא טוקן חדש. */
export const dashboardStatsSchema = z.object({
  openTasks: z.number().int(),
  unassignedTasks: z.number().int(),
  closedThisMonth: z.number().int(),
  overdueUrgent: z.number().int(),
  lowStockItems: z.number().int(),
  /** Decimal מהשרת — מחרוזת, לא number. ראו formatCurrency. */
  revenueThisMonth: money,
});
export type DashboardStats = z.infer<typeof dashboardStatsSchema>;

export const sessionSchema = z.object({
  mustChangePassword: z.boolean(),
  user: sessionUserSchema,
  tenant: sessionTenantSchema,
  /** מפעיל פלטפורמה (קונסולת /admin). הסתרת UI בלבד — השרת בודק בכל בקשה. */
  isPlatformAdmin: z.boolean().optional().default(false),
  /** אימות דו-שלבי דולק למשתמש. */
  mfaEnabled: z.boolean().optional().default(false),
  /** המדיניות מחייבת להקים 2FA לפני שימוש (מנהל, כשהבעלים דרש). */
  mfaRequiredSetup: z.boolean().optional().default(false),
  /** מצב המנוי לבאנרים (src/modules/billing). חסר / null — עסק בלי מנוי או שרת ישן. */
  billing: sessionBillingSchema.nullish(),
});

/**
 * POST /auth/login ו-/auth/change-password.
 * השדה הוא `accessToken`, לא `token` — תואם ל-AuthResponse בשרת.
 */
export const authResponseSchema = sessionSchema.extend({
  accessToken: z.string().min(1),
  /** "לזכור את המכשיר" — רק בתשובת /auth/mfa/verify כשביקשו. */
  deviceToken: z.string().optional(),
});
export type AuthResponse = z.infer<typeof authResponseSchema>;

/** אותו אימייל וסיסמה בכמה עסקים — השרת מבקש לבחור (רק אחרי סיסמה נכונה). */
export const chooseTenantSchema = z.object({
  chooseTenant: z.array(z.object({ subdomain: z.string(), name: z.string() })).min(2),
  /** כניסה עם Google: הכרטיס חד-פעמי, ולכן הבחירה מקבלת כרטיס חדש. */
  ticket: z.string().optional(),
});
export type TenantChoice = z.infer<typeof chooseTenantSchema>['chooseTenant'][number];

/** 2FA חל: במקום טוקן — כרטיס קצר לשלב הקוד (POST /auth/mfa/verify). */
export const mfaChallengeSchema = z.object({
  mfaRequired: z.literal(true),
  mfaTicket: z.string().min(1),
});

export const loginResultSchema = z.union([authResponseSchema, chooseTenantSchema, mfaChallengeSchema]);

export type Session = z.infer<typeof sessionSchema>;
